import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Target, DollarSign, Send, Gamepad2, ChevronRight, 
  ChevronLeft, Plus, Check, AlertCircle, Wallet, Users, Trophy,
  Monitor, Smartphone, Dices, Activity, Zap, Edit3, Camera, Video, Handshake
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SKILL_LEVEL_INFO = {
  beginner: { label: 'Beginner', color: '#00FF94', desc: 'New to competitive play' },
  intermediate: { label: 'Intermediate', color: '#00F0FF', desc: 'Some competitive experience' },
  advanced: { label: 'Advanced', color: '#FFB800', desc: 'Experienced competitor' },
  professional: { label: 'Professional', color: '#FF2E2E', desc: 'Tournament-level player' }
};

// Category icons and labels
const CATEGORY_INFO = {
  console_games: { label: 'Console Games', icon: Gamepad2, color: '#7000FF', desc: 'Xbox, PlayStation, Switch' },
  pc_games: { label: 'PC Games', icon: Monitor, color: '#00F0FF', desc: 'Steam, Epic, etc.' },
  mobile_games: { label: 'Mobile Games', icon: Smartphone, color: '#00FF94', desc: 'iOS & Android' },
  board_games: { label: 'Board Games', icon: Dices, color: '#FFB800', desc: 'Chess, checkers, etc.' },
  sports: { label: 'Sports', icon: Activity, color: '#FF2E2E', desc: 'Physical competitions' },
  fitness: { label: 'Fitness', icon: Zap, color: '#CCFF00', desc: 'Workout challenges' },
  reaction: { label: 'Reaction', icon: Target, color: '#FF6B35', desc: 'Speed & reflex tests' },
  custom: { label: 'Custom', icon: Edit3, color: '#A855F7', desc: 'Define your own' }
};

// Proof type options
const PROOF_TYPE_INFO = {
  screenshot: { label: 'Screenshot', icon: Camera, desc: 'Upload a screenshot of results' },
  photo: { label: 'Photo', icon: Camera, desc: 'Take a photo as proof' },
  video: { label: 'Video', icon: Video, desc: 'Record video evidence' },
  mutual_confirm: { label: 'Mutual Confirm', icon: Handshake, desc: 'Both players confirm result' }
};

const CreateMatch = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [games, setGames] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Form state
  const [selectedGame, setSelectedGame] = useState(null);
  const [isCustomGame, setIsCustomGame] = useState(false);
  const [customGameName, setCustomGameName] = useState('');
  const [entryFee, setEntryFee] = useState(null);
  const [skillLevel, setSkillLevel] = useState('intermediate');
  // New Phase 1 fields
  const [category, setCategory] = useState('console_games');
  const [proofType, setProofType] = useState('screenshot');
  const [customTitle, setCustomTitle] = useState('');
  const [customRules, setCustomRules] = useState('');

  useEffect(() => {
    fetchGamesAndConfig();
  }, []);

  const fetchGamesAndConfig = async () => {
    try {
      const [gamesRes, configRes] = await Promise.all([
        axios.get(`${API}/matches/games`),
        axios.get(`${API}/matches/config`)
      ]);
      setGames(gamesRes.data);
      setConfig(configRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleCreateMatch = async () => {
    if (!selectedGame && !isCustomGame) {
      toast.error('Please select a game');
      return;
    }
    if (isCustomGame && !customGameName.trim()) {
      toast.error('Please enter a game name');
      return;
    }
    if (!entryFee) {
      toast.error('Please select an entry fee');
      return;
    }
    if (!skillLevel) {
      toast.error('Please select your skill level');
      return;
    }
    const rate = config?.credit_conversion_rate || 100;
    const entryCredits = entryFee * rate;
    if ((user?.credits_balance || 0) < entryCredits) {
      toast.error('Insufficient credits');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/matches/create`,
        {
          game_id: isCustomGame ? 'custom' : selectedGame.id,
          wager_amount: entryFee,
          skill_level: skillLevel,
          is_custom_game: isCustomGame,
          custom_game_name: isCustomGame ? customGameName : null,
          // Phase 1: New universal challenge fields
          category: category,
          proof_type: proofType,
          custom_title: customTitle.trim() || null,
          custom_rules: customRules.trim() || null
        },
        { withCredentials: true }
      );

      await refreshUser();
      toast.success('Competition created successfully!');
      navigate(`/match/${response.data.match_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create competition');
    } finally {
      setLoading(false);
    }
  };

  const canProceed = () => {
    if (step === 1) return category && (selectedGame || (isCustomGame && customGameName.trim()));
    const rate = config?.credit_conversion_rate || 100;
    if (step === 2) return entryFee && (entryFee * rate) <= (user?.credits_balance || 0);
    if (step === 3) return skillLevel;
    if (step === 4) return proofType;
    return false;
  };

  const steps = [
    { num: 1, title: 'CATEGORY & GAME', icon: Target },
    { num: 2, title: 'ENTRY FEE', icon: DollarSign },
    { num: 3, title: 'SKILL LEVEL', icon: Users },
    { num: 4, title: 'PROOF TYPE', icon: Camera },
    { num: 5, title: 'CONFIRM', icon: Send },
  ];

  const totalEntry = entryFee ? entryFee * 2 : 0;
  const platformFee = totalEntry * ((config?.platform_fee_percent || 8) / 100);
  const winnerReward = totalEntry - platformFee;

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto" data-testid="create-match-page">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="font-heading text-3xl font-bold mb-2">Create Competition</h1>
          <p className="text-white/50">Set up a skill-based gaming match</p>
        </div>

        {/* Steps Indicator */}
        <div className="flex items-center justify-center gap-2 mb-10 flex-wrap">
          {steps.map((s, i) => (
            <div key={s.num} className="flex items-center">
              <div 
                className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all ${
                  step === s.num 
                    ? 'bg-[#00F0FF]/20 text-[#00F0FF]' 
                    : step > s.num
                      ? 'bg-[#00FF94]/20 text-[#00FF94]'
                      : 'bg-white/5 text-white/40'
                }`}
              >
                {step > s.num ? (
                  <Check className="w-4 h-4" />
                ) : (
                  <s.icon className="w-4 h-4" />
                )}
                <span className="text-xs font-mono uppercase tracking-wider hidden sm:inline">{s.title}</span>
              </div>
              {i < steps.length - 1 && (
                <ChevronRight className="w-4 h-4 text-white/20 mx-1" />
              )}
            </div>
          ))}
        </div>

        {/* Step Content */}
        <motion.div
          key={step}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          className="glass-card rounded-2xl p-8"
        >
          {/* Step 1: Category & Game Selection */}
          {step === 1 && (
            <div>
              <h2 className="font-heading font-semibold text-xl mb-6">Select Category & Game</h2>
              
              {/* Category Selection */}
              <div className="mb-6">
                <label className="text-sm text-white/50 mb-3 block">Challenge Category</label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {Object.entries(CATEGORY_INFO).map(([key, info]) => {
                    const IconComponent = info.icon;
                    return (
                      <motion.button
                        key={key}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => setCategory(key)}
                        className={`p-3 rounded-xl text-left transition-all ${
                          category === key
                            ? 'border-2'
                            : 'bg-white/5 border-2 border-transparent hover:border-white/20'
                        }`}
                        style={{
                          borderColor: category === key ? info.color : 'transparent',
                          backgroundColor: category === key ? `${info.color}15` : ''
                        }}
                        data-testid={`category-${key}`}
                      >
                        <IconComponent 
                          className="w-5 h-5 mb-1" 
                          style={{ color: category === key ? info.color : 'rgba(255,255,255,0.5)' }}
                        />
                        <p className="text-sm font-medium" style={{ color: category === key ? info.color : 'white' }}>
                          {info.label}
                        </p>
                        <p className="text-xs text-white/40">{info.desc}</p>
                      </motion.button>
                    );
                  })}
                </div>
              </div>

              {/* Divider */}
              <div className="border-t border-white/10 my-6" />
              
              {/* Game Selection */}
              <label className="text-sm text-white/50 mb-3 block">Select Game</label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                {games.map(game => (
                  <motion.div
                    key={game.id}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      setSelectedGame(game);
                      setIsCustomGame(false);
                    }}
                    className={`p-4 rounded-xl cursor-pointer transition-all ${
                      selectedGame?.id === game.id && !isCustomGame
                        ? 'bg-[#7000FF]/20 border-2 border-[#7000FF]'
                        : 'bg-white/5 border-2 border-transparent hover:border-white/20'
                    }`}
                    data-testid={`game-option-${game.id}`}
                  >
                    <Gamepad2 className={`w-8 h-8 mx-auto mb-2 ${
                      selectedGame?.id === game.id && !isCustomGame ? 'text-[#7000FF]' : 'text-white/50'
                    }`} />
                    <p className="text-center text-sm font-medium">{game.name}</p>
                  </motion.div>
                ))}
                
                {/* Custom Game Option */}
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    setIsCustomGame(true);
                    setSelectedGame(null);
                  }}
                  className={`p-4 rounded-xl cursor-pointer transition-all ${
                    isCustomGame
                      ? 'bg-[#FFB800]/20 border-2 border-[#FFB800]'
                      : 'bg-white/5 border-2 border-transparent hover:border-white/20'
                  }`}
                  data-testid="custom-game-option"
                >
                  <Plus className={`w-8 h-8 mx-auto mb-2 ${
                    isCustomGame ? 'text-[#FFB800]' : 'text-white/50'
                  }`} />
                  <p className="text-center text-sm font-medium">Custom Game</p>
                </motion.div>
              </div>

              {isCustomGame && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="space-y-4"
                >
                  <div>
                    <label className="text-sm text-white/50 mb-2 block">Game/Challenge Name</label>
                    <Input
                      placeholder="e.g., Cornhole to 21, Push-up challenge..."
                      value={customGameName}
                      onChange={(e) => setCustomGameName(e.target.value)}
                      className="input-dark"
                      data-testid="custom-game-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-white/50 mb-2 block">Custom Title (Optional)</label>
                    <Input
                      placeholder="e.g., Best of 3 rounds..."
                      value={customTitle}
                      onChange={(e) => setCustomTitle(e.target.value)}
                      className="input-dark"
                      data-testid="custom-title-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-white/50 mb-2 block">Rules Description (Optional)</label>
                    <Textarea
                      placeholder="Describe the rules for this challenge..."
                      value={customRules}
                      onChange={(e) => setCustomRules(e.target.value)}
                      className="input-dark min-h-[80px]"
                      data-testid="custom-rules-input"
                    />
                  </div>
                </motion.div>
              )}
            </div>
          )}

          {/* Step 2: Set Entry Fee */}
          {step === 2 && (
            <div>
              <h2 className="font-heading font-semibold text-xl mb-2">Select Entry Fee</h2>
              <p className="text-white/50 text-sm mb-6">
                Playing: <span className="text-[#7000FF]">{isCustomGame ? customGameName : selectedGame?.name}</span>
              </p>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                {config?.entry_fee_tiers?.map((tier) => {
                  const rate = config?.credit_conversion_rate || 100;
                  return (
                    <motion.button
                      key={tier}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setEntryFee(tier)}
                      disabled={(user?.credits_balance || 0) < tier * rate}
                      className={`p-4 rounded-xl transition-all text-center ${
                        entryFee === tier
                          ? 'bg-[#00F0FF]/20 border-2 border-[#00F0FF]'
                          : (user?.credits_balance || 0) < tier * rate
                            ? 'bg-white/5 border-2 border-transparent opacity-50 cursor-not-allowed'
                            : 'bg-white/5 border-2 border-transparent hover:border-white/20 cursor-pointer'
                      }`}
                      data-testid={`entry-fee-${tier}`}
                    >
                      <span className={`font-mono text-2xl font-bold block ${entryFee === tier ? 'text-[#00F0FF]' : 'text-white'}`}>
                        ${tier}
                      </span>
                      <span className="block text-xs text-white/40 font-medium tracking-wider">ENTRY</span>
                    </motion.button>
                  );
                })}
              </div>

              <div className="space-y-3">
                <div className="flex justify-between p-3 rounded-lg bg-white/5">
                  <span className="text-white/50">Your Balance</span>
                  <span className="font-mono text-[#00F0FF]">${((user?.credits_balance || 0) / (config?.credit_conversion_rate || 100)).toFixed(2)}</span>
                </div>
                {entryFee && (
                  <>
                    <div className="flex justify-between p-3 rounded-lg bg-white/5">
                      <span className="text-white/50">Your Entry</span>
                      <span className="font-mono">${entryFee.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between p-3 rounded-lg bg-white/5">
                      <span className="text-white/50">Total Match Pool</span>
                      <span className="font-mono">${(entryFee * 2).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between p-3 rounded-lg bg-white/5">
                      <span className="text-white/50">MatchStake Service Fee ({config?.platform_fee_percent || 5}%)</span>
                      <span className="font-mono text-[#7000FF]">-${platformFee.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between p-3 rounded-lg bg-[#00FF94]/10 border border-[#00FF94]/20">
                      <span className="text-[#00FF94] font-medium">Winner Receives</span>
                      <span className="font-mono text-[#00FF94] font-bold">
                        ${winnerReward.toFixed(2)}
                      </span>
                    </div>
                  </>
                )}
              </div>

              {entryFee && (user?.credits_balance || 0) < entryFee * (config?.credit_conversion_rate || 100) && (
                <div className="mt-4 p-4 rounded-lg bg-[#FF2E2E]/10">
                  <div className="flex items-center gap-2 mb-3">
                    <AlertCircle className="w-4 h-4 text-[#FF2E2E]" />
                    <span className="text-sm text-[#FF2E2E]">Insufficient credits</span>
                  </div>
                  <Button
                    onClick={() => navigate('/wallet')}
                    className="w-full bg-[#00F0FF] text-black hover:bg-[#00F0FF]/90"
                    data-testid="purchase-credits-btn"
                  >
                    <Wallet className="w-4 h-4 mr-2" />
                    Purchase Credits
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Skill Level */}
          {step === 3 && (
            <div>
              <h2 className="font-heading font-semibold text-xl mb-2">Select Your Skill Level</h2>
              <p className="text-white/50 text-sm mb-6">
                Be honest - this helps ensure fair matchups
              </p>

              <div className="grid grid-cols-2 gap-4 mb-6">
                {Object.entries(SKILL_LEVEL_INFO).map(([key, info]) => (
                  <motion.button
                    key={key}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setSkillLevel(key)}
                    className={`p-4 rounded-xl transition-all text-left ${
                      skillLevel === key
                        ? 'border-2'
                        : 'bg-white/5 border-2 border-transparent hover:border-white/20'
                    }`}
                    style={{
                      borderColor: skillLevel === key ? info.color : 'transparent',
                      backgroundColor: skillLevel === key ? `${info.color}15` : ''
                    }}
                    data-testid={`skill-level-${key}`}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <div 
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: info.color }}
                      />
                      <span className="font-semibold" style={{ color: skillLevel === key ? info.color : 'white' }}>
                        {info.label}
                      </span>
                    </div>
                    <p className="text-white/50 text-sm">{info.desc}</p>
                  </motion.button>
                ))}
              </div>

              {/* Skill-based competition notice */}
              <div className="p-4 rounded-xl bg-white/5 text-sm text-white/60">
                <p>
                  MatchStake hosts skill-based competitive gaming matches. Match outcomes are 
                  determined by player skill and in-game performance.
                </p>
              </div>
            </div>
          )}

          {/* Step 4: Proof Type */}
          {step === 4 && (
            <div>
              <h2 className="font-heading font-semibold text-xl mb-2">Select Proof Type</h2>
              <p className="text-white/50 text-sm mb-6">
                How will you verify the match result?
              </p>

              <div className="grid grid-cols-2 gap-4 mb-6">
                {Object.entries(PROOF_TYPE_INFO).map(([key, info]) => {
                  const IconComponent = info.icon;
                  return (
                    <motion.button
                      key={key}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setProofType(key)}
                      className={`p-4 rounded-xl transition-all text-left ${
                        proofType === key
                          ? 'bg-[#00F0FF]/20 border-2 border-[#00F0FF]'
                          : 'bg-white/5 border-2 border-transparent hover:border-white/20'
                      }`}
                      data-testid={`proof-type-${key}`}
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <IconComponent className={`w-5 h-5 ${proofType === key ? 'text-[#00F0FF]' : 'text-white/50'}`} />
                        <span className={`font-semibold ${proofType === key ? 'text-[#00F0FF]' : 'text-white'}`}>
                          {info.label}
                        </span>
                      </div>
                      <p className="text-white/50 text-sm">{info.desc}</p>
                    </motion.button>
                  );
                })}
              </div>

              {/* Proof type explanation */}
              <div className="p-4 rounded-xl bg-white/5 text-sm text-white/60">
                <p>
                  {proofType === 'screenshot' && 'Upload a screenshot showing the final score or result screen.'}
                  {proofType === 'photo' && 'Take a photo of the scoreboard, game result, or competition outcome.'}
                  {proofType === 'video' && 'Record a video clip showing the match result or final moments.'}
                  {proofType === 'mutual_confirm' && 'Both players will confirm the result without requiring proof upload.'}
                </p>
              </div>
            </div>
          )}

          {/* Step 5: Confirm */}
          {step === 5 && (
            <div>
              <h2 className="font-heading font-semibold text-xl mb-6">Confirm Match</h2>
              
              <div className="space-y-4 mb-8">
                {/* Category */}
                <div className="flex items-center justify-between p-4 rounded-xl bg-white/5">
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${CATEGORY_INFO[category]?.color}20` }}
                    >
                      {CATEGORY_INFO[category] && (() => {
                        const CategoryIcon = CATEGORY_INFO[category].icon;
                        return <CategoryIcon className="w-5 h-5" style={{ color: CATEGORY_INFO[category].color }} />;
                      })()}
                    </div>
                    <span className="text-white/50">Category</span>
                  </div>
                  <span className="font-semibold" style={{ color: CATEGORY_INFO[category]?.color }}>
                    {CATEGORY_INFO[category]?.label}
                  </span>
                </div>

                {/* Game */}
                <div className="flex items-center justify-between p-4 rounded-xl bg-white/5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#7000FF]/20 flex items-center justify-center">
                      <Gamepad2 className="w-5 h-5 text-[#7000FF]" />
                    </div>
                    <span className="text-white/50">Game</span>
                  </div>
                  <span className="font-semibold">{isCustomGame ? customGameName : selectedGame?.name}</span>
                </div>

                {/* Your Entry */}
                <div className="flex items-center justify-between p-4 rounded-xl bg-white/5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#CCFF00]/20 flex items-center justify-center">
                      <DollarSign className="w-5 h-5 text-[#CCFF00]" />
                    </div>
                    <span className="text-white/50">Your Entry</span>
                  </div>
                  <span className="font-mono text-2xl font-bold text-[#00F0FF]">
                    ${entryFee?.toFixed(2)}
                  </span>
                </div>

                {/* Skill Level */}
                <div className="flex items-center justify-between p-4 rounded-xl bg-white/5">
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${SKILL_LEVEL_INFO[skillLevel].color}20` }}
                    >
                      <Users className="w-5 h-5" style={{ color: SKILL_LEVEL_INFO[skillLevel].color }} />
                    </div>
                    <span className="text-white/50">Skill Level</span>
                  </div>
                  <span className="font-semibold" style={{ color: SKILL_LEVEL_INFO[skillLevel].color }}>
                    {SKILL_LEVEL_INFO[skillLevel].label}
                  </span>
                </div>

                {/* Proof Type */}
                <div className="flex items-center justify-between p-4 rounded-xl bg-white/5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#00F0FF]/20 flex items-center justify-center">
                      {PROOF_TYPE_INFO[proofType] && (() => {
                        const ProofIcon = PROOF_TYPE_INFO[proofType].icon;
                        return <ProofIcon className="w-5 h-5 text-[#00F0FF]" />;
                      })()}
                    </div>
                    <span className="text-white/50">Proof Type</span>
                  </div>
                  <span className="font-semibold text-[#00F0FF]">
                    {PROOF_TYPE_INFO[proofType]?.label}
                  </span>
                </div>

                {/* Winner Receives */}
                <div className="p-4 rounded-xl bg-[#00FF94]/10 border border-[#00FF94]/20">
                  <div className="flex justify-between items-center">
                    <span className="flex items-center gap-2">
                      <Trophy className="w-5 h-5 text-[#00FF94]" />
                      Winner Receives
                    </span>
                    <span 
                      className="font-mono text-2xl font-bold text-[#00FF94]"
                      style={{ textShadow: '0 0 20px rgba(0, 255, 148, 0.5)' }}
                    >
                      ${winnerReward.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              <p className="text-white/40 text-sm text-center mb-6">
                Both players enter the same match amount. The winner receives the payout after match confirmation.
              </p>

              {/* Service fee note */}
              <div className="p-4 rounded-xl bg-white/5 border border-white/10 text-sm">
                <p className="text-white/50">
                  A {config?.platform_fee_percent || 5}% MatchStake service fee is applied to the total match pool.
                </p>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-white/10">
            {step > 1 ? (
              <Button
                variant="outline"
                onClick={() => setStep(step - 1)}
                className="btn-secondary"
                data-testid="back-btn"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
            ) : (
              <Button
                variant="outline"
                onClick={() => navigate('/lobby')}
                className="btn-secondary"
                data-testid="cancel-btn"
              >
                Cancel
              </Button>
            )}

            {step < 5 ? (
              <Button
                onClick={() => setStep(step + 1)}
                disabled={!canProceed()}
                className="btn-primary clip-slant-sm"
                data-testid="next-btn"
              >
                Continue
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button
                onClick={handleCreateMatch}
                disabled={loading}
                className="btn-primary clip-slant"
                data-testid="create-match-submit-btn"
              >
                {loading ? (
                  'Creating...'
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Create Competition
                  </>
                )}
              </Button>
            )}
          </div>
        </motion.div>
      </div>
    </DashboardLayout>
  );
};

export default CreateMatch;
